<template lang="pug">
div
  div.text-lg.font-medium.neutral700--text.mt-8 {{ $t('provider_availability') }}
  div.text-sm.neutral500--text {{ $t('provider_availability_desc') }}

  div.text-md.font-medium.neutral700--text.mt-8 {{ $t('configure_times_available') }}
  v-card.round-xl.mt-4(outlined)
    v-row(no-gutters)
      v-col.pa-6(cols="12" sm="7")
        div.text-md.font-medium {{ $t('set_weekly_hours') }}
        div.mt-4
          template(v-for="(schedule, scheindex) in schedules")
            v-row(no-gutters :key="'schedule-' + scheindex")
              v-col.shrink
                div(style="min-width: 100px;")
                  y-checkbox.mt-3(v-model="schedule.enabled" @change="onCheck(schedule.day)")
                    template(#label)
                      div.text-md.font-semibold.text-uppercase.neutral700--text.ml-2 {{ schedule.day }}
              template(v-if="schedule.slots.length > 0")
                v-col
                  div.d-flex.mb-3(v-for="(slot, slindex) in schedule.slots" :key="'slot-' + slindex")
                    v-text-field.text-md.font-normal.round-xl(v-model="slot.begin" type="time" outlined :rules="rules.not_empty" dense height="48" hide-details="hide")
                    div.mt-3.mx-3 -
                    v-text-field.text-md.font-normal.round-xl(v-model="slot.end" type="time" outlined :rules="rules.not_empty" dense height="48" hide-details="hide")
                    v-btn.mt-3.ml-3(v-if="schedule.slots.length > 1" small icon @click="deleteSlot(slindex, schedule.slots)")
                      v-icon(color="neutral700" size="18") $yzi-trash-03
                v-col.shrink
                  div.d-flex.justify-end(style="min-width: 100px;")
                    v-btn.mt-3(small icon @click="addSlot(schedule.slots)")
                      v-icon(color="neutral700" size="18") $yzi-plus
                    v-menu(v-model="schedule.menu" offset-y left attach :close-on-content-click="false")
                      template(#activator="{ on, attrs }")
                        v-btn.mt-3.ml-3(small icon v-bind="attrs" v-on="on")
                          v-icon(color="neutral700" size="18") $yzi-copy-03
                      v-card(min-width="250")
                        div.text-xs.font-bold.neutral700--text.px-4.py-3 {{ $t('copy_timing_to') }}...
                        template(v-for="copy in copySlot")
                          v-divider
                          div.px-4.py-3
                            y-checkbox(v-model="copy.selected")
                              template(#label)
                                div.text-sm.neutral700-text.text-uppercase {{ copy.day }}
                        div.d-flex.align-center.px-4.py-3
                          div.text-xs.font-semibold.underline.normal-case.neutral700--text.clickable(@click="clearCopySlot") {{ $t('clear') }}
                          v-spacer
                          v-btn.button-xs(color="neutral700" dark @click="copyTime(schedule)") {{ $t('apply') }}
                      
              template(v-else)
                v-col
                  div.text-md.neutral500--text.mt-3.mb-6 {{ $t('unavailable') }}
            v-divider.mb-4(v-if="scheindex !== (schedules.length - 1)")

      v-divider(v-if="$vuetify.breakpoint.smAndUp" vertical)
      v-col.grow
        div.pa-6
          div.text-md.font-medium.neutral700--text {{ $t('add_date_overrides') }}
          div.text-md.neutral500--text.mt-4 {{ $t('add_date_overrides_desc') }}
          v-btn.button-md.mt-4(color="neutral700" block outlined @click="openOverrideDialog = true") {{ $t('add_date_override') }}

        div.d-flex.justify-center.mt-16(v-if="isFetching")
          v-progress-circular(:width="2" :size="50" color="primary" indeterminate)
        template(v-else-if="overrideDates.length > 0")
          template(v-for="(overrideDate, ovindex) in overrideDates")
            v-divider
            div.px-6.py-4(:key="'override-' + ovindex")
              v-row(no-gutters)
                v-col
                  div.text-sm.neutral700--text {{ [overrideDate.begin_date, overrideDate.end_date] | humanizeDateRange }}
                v-col
                  template(v-for="(oslot, osindex) in overrideDate.slots")
                    div.text-sm.neutral700--text.mb-1(:key="'overslot-' + osindex") {{ oslot.begin | displayTime }} - {{ oslot.end | displayTime }}
                  template(v-if="overrideDate.slots.length === 0")
                    div.text-sm.neutral700--text {{ $t('unavailable') }}
                v-col.shrink
                  v-btn.ml-3(small icon @click="deleteOverride(ovindex, overrideDates)")
                    v-icon(color="neutral700" size="18") $yzi-trash-03

  .modals
    override-date-dialog(v-if="openOverrideDialog" :open.sync="openOverrideDialog" @addNewOverride="addNewOverride($event)")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import OverrideDateDialog from '@/components/clinic/dialogs/OverrideDateDialog.vue'

export default {
  name: 'ProviderAvailability',
  components: { OverrideDateDialog },
  data () {
    return {
      isLoading: false,
      isFetching: false,
      openOverrideDialog: false,
      overrideDates: [],
      schedules: [
        { day: 'Sun', slots: [], enabled: false, menu: false },
        { day: 'Mon', slots: [], enabled: false, menu: false },
        { day: 'Tue', slots: [], enabled: false, menu: false },
        { day: 'Wed', slots: [], enabled: false, menu: false },
        { day: 'Thu', slots: [], enabled: false, menu: false },
        { day: 'Fri', slots: [], enabled: false, menu: false },
        { day: 'Sat', slots: [], enabled: false, menu: false }
      ],
      copySlot: [
        { day: 'Sun', selected: false },
        { day: 'Mon', selected: false },
        { day: 'Tue', selected: false },
        { day: 'Wed', selected: false },
        { day: 'Thu', selected: false },
        { day: 'Fri', selected: false },
        { day: 'Sat', selected: false }
      ],
      rules: {
        not_empty: [val => (val || '').length > 0 || 'This field is required']
      },
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      provider: 'clinic/getProvider'
    }),
    availabilityData () {
      return {
        schedules: this.schedules.map(m => { return { day: m.day, slots: m.slots }}),
        date_overrides: this.overrideDates
      }
    }
  },
  watch: {
    availabilityData: {
      deep: true,
      handler (val) {
        this.$store.commit('clinic/updateTempProvider', val)
      } 
    }
  },
  mounted () {
    if (this.provider) {
      this.setData(this.provider)
    }
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchDateOverrides () {
      this.isFetching = true
      this.$axios.get(`/clinics/${this.company.slug}/providers/${this.provider.id}/date_overrides`)
        .then(resp => {
          this.overrideDates = resp.data
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isFetching = false
        })
    },
    clickBack () {
      this.$router.go(-1)
    },
    setData (provider) {
      if (provider.schedules.length > 0) {
        this.schedules = provider.schedules.map(m => {
          let enabled = false
          if (m.slots.length > 0) {
            enabled = true
          }
          return { ...m, enabled, menu: false }
        })
      }
      this.fetchDateOverrides()
    },
    onCheck (day) {
      let selected = this.schedules.find(f => f.day === day)
      let index = this.schedules.findIndex(f => f.day === day)
      if (selected) {
        if (selected.slots.length > 0) {
          this.$set(this.schedules[index], 'slots', [])
        } else {
          this.schedules[index].slots.push({ begin: '09:00:00', end: '17:00:00' })
        }
      }
    },
    copyTime (schedule) {
      let newSlots = JSON.parse(JSON.stringify(schedule.slots.map(m => { return { begin: m.begin, end: m.end } })))
      let days = this.copySlot.filter(f => f.selected).map(m => m.day)
      days.forEach(o => {
        let getDay = this.schedules.find(f => f.day === o)
        if (getDay) {
          this.$set(getDay, 'slots', JSON.parse(JSON.stringify(newSlots)))
          this.$set(getDay, 'enabled', true)
        }
      })
      this.clearCopySlot()
      schedule.menu = false
    },
    clearCopySlot () {
      this.copySlot = [
        { day: 'Sun', selected: false },
        { day: 'Mon', selected: false },
        { day: 'Tue', selected: false },
        { day: 'Wed', selected: false },
        { day: 'Thu', selected: false },
        { day: 'Fri', selected: false },
        { day: 'Sat', selected: false }
      ]
    },
    addSlot (slots) {
      let lastSlot = slots[slots.length - 1]
      if (lastSlot.end) {
        let newBegin = Number(lastSlot.end.substr(0, 2)) + 1
        let newEnd = newBegin + 1
        let strNewBegin = newBegin.toString().length > 1 ? (newBegin.toString() + ':00:00') : '0' + (newBegin.toString() + ':00:00')
        let strNewEnd = newEnd.toString().length > 1 ? (newEnd.toString() + ':00:00') : '0' + (newEnd.toString() + ':00:00')
        if (newEnd < 24) {
          slots.push({ begin: strNewBegin, end: strNewEnd })
          return
        }
      }
      slots.push({ begin: null, end: null })
    },
    deleteSlot (index, slots) {
      slots.splice(index, 1)
    },
    deleteOverride (index, data) {
      if (this.provider) {
        const endpoint = `/clinics/${this.company.slug}/providers/${this.provider.id}/date_overrides/${data[index].id}`
        this.$axios.delete(endpoint)
          .then(() => {
            this.updateActivityLog(endpoint, null, null, 'DELETE', this.provider.id)
            this.fetchDateOverrides()
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
      } else {
        data.splice(index, 1)
      }
    },
    addNewOverride (data) {
      if (this.provider) {
        this.fetchDateOverrides()
      } else {
        this.overrideDates.push(data)
      }
    },
    updateActivityLog (endpoint, payload, response, method, id) {
      let data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: 'provider',
        activity: 'Update provider (provider availability)',
        raw_data: { endpoint, payload, response: response ? response.data : null, method}
      }
      this.$axios.post('/users/logs', data)
        .catch(() => {})
    }
  }
}
</script>

<style scoped>
.file-change-layout {
  position: relative;
}
.input-change-layout {
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
  opacity: 0;
}
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-basic500);
}
.error--text >>> fieldset {
  border-color: var(--v-danger);
}
.border-menu {
  border: 1px solid var(--v-basic500);
  box-shadow: none;
}
</style>